import {
  getSubjectsFromClasses,
  getTaskTypesForSubject,
  getResponseTypesForSubject,
  getStates as fetchStates,
} from '../service';

let subjectsCache = {};
let taskTypesCache = {};
let responseTypesCache = {};
let statesCache = null;

const normalizeYearNum = (y) => {
  if (y == null) return null;
  const n =
    typeof y === 'number'
      ? y
      : parseInt(
          String(y)
            .replace(/year\s*/i, '')
            .trim(),
          10
        );
  return Number.isNaN(n) ? null : n;
};

export const clearSubjectsCache = () => {
  subjectsCache = {};
  taskTypesCache = {};
  responseTypesCache = {};
  statesCache = null;
};

/**
 * Normalize a state code or list of state codes to an array of trimmed,
 * uppercased, non-empty codes. Accepts a string, an array, or null/undefined.
 */
export const normalizeStateCodes = (stateOrStates) =>
  (Array.isArray(stateOrStates)
    ? stateOrStates
    : stateOrStates
    ? [stateOrStates]
    : []
  )
    .map((s) => s?.toUpperCase?.()?.trim())
    .filter(Boolean);

/**
 * True if a subject (or tool metadata) is available in ANY of the given
 * already-normalized (uppercased) state codes.
 *
 * A subject with no `states` is treated as universal/national and always
 * matches. When `stateNorms` is empty, everything matches so nothing is
 * filtered out — keeping school users and users with no selected state
 * unaffected.
 */
export const subjectMatchesStates = (subject, stateNorms) => {
  if (!stateNorms || stateNorms.length === 0) return true;
  // The subjects API has emitted more than one shape for states over time:
  // ['NSW'], 'NSW', and [{ code: 'NSW' }]. A scalar string would throw on
  // .some(), and an object entry stringifies to '[object Object]' and silently
  // filters the subject out — so normalize every shape to a code list first.
  const raw = subject?.states;
  const states = (Array.isArray(raw) ? raw : raw ? [raw] : [])
    .map((st) =>
      typeof st === 'string' ? st : st?.code ?? st?.state ?? st?.name ?? ''
    )
    .map((st) => String(st).toUpperCase().trim())
    .filter(Boolean);
  if (states.length === 0) return true;
  return states.some((st) => stateNorms.includes(st));
};

/**
 * Filter an API-subjects array down to those available in the given state(s).
 * No-op (returns the input) when no states are given, so callers that pass an
 * empty scope get the full catalog exactly as before.
 */
export const filterSubjectsByStates = (subjects, stateOrStates) => {
  const stateNorms = normalizeStateCodes(stateOrStates);
  if (!stateNorms.length) return subjects || [];
  return (subjects || []).filter((s) => subjectMatchesStates(s, stateNorms));
};

/**
 * Compute the set of UPPERCASE subject codes to HIDE from a marking-tools list
 * filter because they belong to states other than the user's scope.
 *
 *   hidden = catalog subjects that DO NOT match the scope states
 *            minus codes that appear on the user's own (non-system) tools
 *
 * Returns null (no scoping) when `states` is empty — so school users, stateless
 * users, and any caller that passes [] see an unchanged filter. The teacher's
 * own tools are never hidden, and codes not present in the catalog are left
 * alone (we only hide subjects we can positively place in another state).
 *
 * @param {Array} apiSubjects - the subject catalog (each with `states`)
 * @param {Array|string} states - the user's scope state(s)
 * @param {Array} items - the marking-tool list items (to exempt own tools)
 * @returns {Set<string>|null}
 */
export const computeHiddenSubjectCodes = (apiSubjects, states, items) => {
  const stateNorms = normalizeStateCodes(states);
  if (!stateNorms.length) return null;
  const hidden = new Set();
  (apiSubjects || []).forEach((s) => {
    const code = String(s?.subjectCode || s?.code || '').toUpperCase();
    if (code && !subjectMatchesStates(s, stateNorms)) hidden.add(code);
  });
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (item?.isSystem) return;
    const subs = item?.metadata?.subjects?.length ? item.metadata.subjects : (item?.subjects || []);
    (subs || []).forEach((s) => {
      const code = String(typeof s === 'string' ? s : (s?.subjectCode || s?.code || '')).toUpperCase();
      if (code) hidden.delete(code);
    });
  });
  return hidden.size ? hidden : null;
};

/**
 * The subject catalog's names keyed by upper-cased code, for labelling tools
 * that stored a subject's code as its name (JED-1218).
 *
 * @param {Array} apiSubjects - the subject catalog
 * @returns {Map<string, string>}
 */
export const subjectNamesByCodeOf = (apiSubjects) =>
  new Map(
    (apiSubjects || [])
      .filter((s) => (s?.subjectCode || s?.code) && s?.subjectName)
      .map((s) => [
        String(s.subjectCode || s.code).toUpperCase(),
        s.subjectName,
      ])
  );

const subjectCodeOf = (s) =>
  (typeof s === 'object' && s !== null) ? (s.subjectCode || s.code || s.value) : s;

/**
 * Ensure the currently-selected subjects remain in a create/edit picker's
 * options even when state/year scoping would exclude them — so editing a tool
 * tagged with an out-of-scope subject never silently strips it on save. New
 * choices stay restricted to `base`; only already-selected subjects are
 * re-added, labelled from the tool's original subject objects when available.
 *
 * @param {Array} base - the (state/year scoped) subject options
 * @param {Array} selectedSubjects - currently selected subjects (codes or objects)
 * @param {Array} rawItemSubjects - the tool's original subjects (for labels)
 */
export const withSelectedSubjectsPreserved = (base, selectedSubjects, rawItemSubjects) => {
  const list = Array.isArray(base) ? base : [];
  const raw = Array.isArray(rawItemSubjects) ? rawItemSubjects : [];
  const existing = new Set(list.map(subjectCodeOf).filter(Boolean));
  const added = new Set();
  const missing = (Array.isArray(selectedSubjects) ? selectedSubjects : [])
    .map(subjectCodeOf)
    .filter((code) => code && !existing.has(code) && !added.has(code) && added.add(code))
    .map((code) => {
      const orig = raw.find((s) => subjectCodeOf(s) === code);
      const title = (orig && typeof orig === 'object')
        ? (orig.subjectName || orig.title || orig.name || orig.label || code)
        : code;
      return { code, title, subjectCode: code, subjectName: title };
    });
  return [...list, ...missing];
};

export const fetchSubjectsByState = async (stateOrStates, year) => {
  const statesArr = Array.isArray(stateOrStates)
    ? stateOrStates
    : stateOrStates
    ? [stateOrStates]
    : [];
  const cacheKey = `${statesArr.sort().join(',')}_${year || 'all'}`;
  if (subjectsCache[cacheKey]) return subjectsCache[cacheKey];
  try {
    const data = await getSubjectsFromClasses();
    const yearNum = normalizeYearNum(year);
    const stateNorms = statesArr
      .map((s) => s?.toUpperCase?.()?.trim())
      .filter(Boolean);
    const filtered = (data || []).filter((s) => {
      if (
        stateNorms.length &&
        s.states?.length &&
        !s.states.some((st) => stateNorms.includes(String(st).toUpperCase()))
      )
        return false;
      if (yearNum != null && s.years?.length && !s.years.includes(yearNum))
        return false;
      return true;
    });
    subjectsCache[cacheKey] = filtered;
    return filtered;
  } catch (error) {
    console.error('Error fetching subjects:', error);
    return [];
  }
};

export const getSubjectsByState = (subjects) => {
  if (!subjects || !Array.isArray(subjects)) return [];
  return subjects.map((s) => ({ title: s.subjectName, code: s.subjectCode }));
};

export const getSubjectCode = (subjects, subjectTitle) => {
  if (!subjects || !Array.isArray(subjects)) return '';
  const subject = subjects.find((s) => s.subjectName === subjectTitle);
  return subject?.subjectCode ?? '';
};

export const buildMetadata = (
  stateOrStates,
  settings = {},
  subjectsList = []
) => {
  const {
    years = [],
    subjects: selectedSubjects = [],
    taskTypes = [],
    responseTypes = [],
  } = settings;
  const subjects = selectedSubjects
    .map((s) => toSubjectObject(s, subjectsList))
    .filter((s) => s && s.subjectCode);
  const states = Array.isArray(stateOrStates)
    ? stateOrStates.filter(Boolean)
    : stateOrStates
    ? [stateOrStates]
    : [];
  return {
    subjects,
    states,
    years,
    taskTypes,
    responseTypes,
    tags: [],
    isUniversal: false,
  };
};

const resolveSubjectName = (code, subjectsList) => {
  if (!Array.isArray(subjectsList) || subjectsList.length === 0) return code;
  const s = subjectsList.find(
    (sub) => String(sub.subjectCode ?? '') === String(code)
  );
  return s ? s.subjectName : code;
};

const resolveSubjectCode = (value, subjectsList) => {
  if (!Array.isArray(subjectsList) || subjectsList.length === 0) return value;
  const str = String(value ?? '');
  const byCode = subjectsList.find(
    (sub) => String(sub.subjectCode ?? '') === str
  );
  if (byCode) return byCode.subjectCode;
  const byName = subjectsList.find(
    (sub) =>
      String(sub.subjectName ?? '')
        .toLowerCase()
        .trim() === str.toLowerCase().trim()
  );
  return byName ? byName.subjectCode : value;
};

const toSubjectObject = (item, subjectsList) => {
  if (item == null) return null;
  if (typeof item === 'object' && item !== null) {
    const rawCode = item.subjectCode ?? '';
    const rawName = item.subjectName ?? null;
    const resolvedCode = resolveSubjectCode(rawCode || rawName, subjectsList);
    return {
      subjectCode: resolvedCode,
      subjectName:
        rawName ??
        resolveSubjectName(resolvedCode, subjectsList) ??
        resolvedCode,
    };
  }
  const raw = String(item);
  const resolvedCode = resolveSubjectCode(raw, subjectsList);
  return {
    subjectCode: resolvedCode,
    subjectName: resolveSubjectName(resolvedCode, subjectsList) || resolvedCode,
  };
};

export const toSubjectsForApi = (subjects = [], subjectsList = []) =>
  (Array.isArray(subjects) ? subjects : [])
    .map((s) => toSubjectObject(s, subjectsList))
    .filter((s) => s && s.subjectCode);

export const getCloneMetadata = (source, subjectsList) => {
  if (!source)
    return {
      states: [],
      years: [],
      subjects: [],
      taskTypes: [],
      responseTypes: [],
      metadata: undefined,
    };
  const meta = source.metadata || {};
  const states = Array.isArray(meta.states) ? meta.states : [];
  const years = Array.isArray(meta.years) ? meta.years : [];
  const rawSubjects = meta.subjects;
  const subjects = (Array.isArray(rawSubjects) ? rawSubjects : [])
    .map((s) => toSubjectObject(s, subjectsList))
    .filter((s) => s && s.subjectCode);
  const taskTypes = Array.isArray(meta.taskTypes) ? meta.taskTypes : [];
  const responseTypes = Array.isArray(meta.responseTypes)
    ? meta.responseTypes
    : [];
  const metadata =
    states.length ||
    subjects.length ||
    years.length ||
    taskTypes.length ||
    responseTypes.length
      ? { states, subjects, years, taskTypes, responseTypes }
      : undefined;
  return { states, years, subjects, taskTypes, responseTypes, metadata };
};

/**
 * Build the request body for cloning a marking tool (comment bank, success
 * criteria, short answer, marking criteria).
 *
 * Spreads the source's metadata fields (states, years, subjects, taskTypes,
 * responseTypes) plus a derived `metadata` object so the new record inherits
 * the source's classification. When the source has NO populated metadata
 * fields, the `metadata` key is `undefined` and JSON.stringify omits it —
 * the backend's create-side defaults (CommentBanksRepositoryEventListener,
 * SuccessCriterias/ShortAnswers domain compact constructors,
 * MarkingCriteriasRepositoryImpl.metadataOrDefault) then default it to
 * universal so the saved record is never null.
 *
 * @param {Object} source - The tool being cloned. Must have `.title` and
 *   ideally `.metadata`.
 * @param {Array}  subjectsList - The full API subjects list for code
 *   resolution. Pass `[]` when not available; existing Subject objects pass
 *   through unchanged.
 * @param {Object} extras - Tool-specific payload fields, e.g.
 *   `{ smartComments }`, `{ criterias }`, `{ highestMarks, criterias }`,
 *   `{ type, criterias }` for marking criteria.
 * @returns {Object} Payload object ready to POST to the create endpoint.
 */
export const buildClonePayload = (source, subjectsList = [], extras = {}) => {
  const title = `Copy of ${source?.title ?? ''}`;
  const cloneMeta = getCloneMetadata(source, subjectsList);
  return { title, ...extras, ...cloneMeta };
};
